/**
 * Geocodificador especializado para infraestructuras sanitarias de Andalucía
 * 
 * Conecta con servicios WFS de IECA/DERA para acceder a:
 * - SICESS: Sistema de Información de Centros Sanitarios (1,500+ centros)
 * - SAS: Servicio Andaluz de Salud
 * 
 * Fuentes oficiales:
 * - WFS DERA G12: https://www.ideandalucia.es/services/DERA_g12_servicios/wfs
 * - Capas: g12_01_CentroSalud, g12_02_Hospital_CAE
 *
 * Nota: la capa g12_03_Consultorio fue retirada — el WFS DERA G12 real consolida
 * los consultorios rurales del SAS bajo g12_01_CentroSalud sin distinción asistencial
 * (verificado empíricamente smoke 11-may: Castril 18046 = 3 CS, Colomera 18051 = 2 CS).
 * Investigación SIPOB/SAS pendiente en OBS-FT-SAN-CONSULTORIOS-FUENTE-AUSENTE.
 *
 * @module services/geocoding/specialized
 */

import type { 
  WFSFeature, 
  WFSSearchOptions 
} from './WFSBaseGeocoder';
import { 
  WFSBaseGeocoder 
} from './WFSBaseGeocoder';
import type { GeoJSONFeature } from '../../../types/geojson';
import { getStringProp } from '../../../types/geojson';
import type { SpecializedGeocoderConfig } from '../../../types/infrastructure';

/**
 * Tipos de centros sanitarios en Andalucía
 */
export enum HealthFacilityType {
  /** Centros de Salud (~400 en Andalucía) */
  HEALTH_CENTER = 'CENTRO_SALUD',
  
  /** Hospitales (~40 en Andalucía) */
  HOSPITAL = 'HOSPITAL',
  
  // Nota: HealthFacilityType.CONSULTORIO retirado — la capa g12_03_Consultorio no
  // existe en el WFS DERA G12 real (smoke 11-may, HTTP 400 sistemático). Los
  // consultorios rurales del SAS están consolidados bajo g12_01_CentroSalud,
  // cubiertos por HEALTH_CENTER. Investigación SIPOB/SAS pendiente en
  // OBS-FT-SAN-CONSULTORIOS-FUENTE-AUSENTE.

  /** Ambulatorios */
  AMBULATORIO = 'AMBULATORIO'
}

/**
 * Opciones de búsqueda específicas para sanitarios
 */
export interface HealthSearchOptions extends WFSSearchOptions {
  /** Tipo de centro sanitario a buscar */
  facilityType?: HealthFacilityType;
  
  /** Distrito sanitario (Almería, Granada, etc) */
  healthDistrict?: string;
}

/**
 * Geocodificador especializado para centros sanitarios andaluces
 * 
 * Precisión esperada: ±2-10m (coordenadas oficiales SAS)
 * Cobertura: ~1,500 infraestructuras sanitarias
 * 
 * @example
 * ```typescript
 * const geocoder = new WFSHealthGeocoder();
 * const result = await geocoder.geocode({
 *   name: 'Centro de Salud San Antón',
 *   municipality: 'Granada',
 *   province: 'Granada'
 * });
 * // result.confidence >= 85 (match oficial SAS)
 * ```
 */
export class WFSHealthGeocoder extends WFSBaseGeocoder {
  
  /**
   * Configuración específica para servicios WFS sanitarios de IECA
   */
  protected getDefaultConfig(): SpecializedGeocoderConfig {
    return {
      wfsEndpoint: 'https://www.ideandalucia.es/services/DERA_g12_servicios/wfs',
      layerName: 'g12_01_CentroSalud', // Capa por defecto: Centros de Salud
      fuzzyThreshold: 0.3, // Threshold permisivo para variaciones nombre
      timeout: 15000, // 15s para WFS IECA
      outputSRS: 'EPSG:25830' // UTM30 ETRS89
    };
  }

  /**
   * Parsea feature GeoJSON de DERA G12 a formato interno
   * 
   * Estructura esperada de DERA G12:
   * {
   *   type: "Feature",
   *   geometry: { type: "Point", coordinates: [x, y] },
   *   properties: {
   *     DENOMINACION: "Centro de Salud San Antón",
   *     MUNICIPIO: "Granada",
   *     PROVINCIA: "Granada",
   *     DIRECCION: "Calle San Antón 72",
   *     TIPO_CENTRO: "Centro de Salud",
   *     DISTRITO_SANITARIO: "Granada"
   *   }
   * }
   */
  protected parseFeature(feature: GeoJSONFeature): WFSFeature | null {
    try {
      const props = feature.properties || {};
      const geom = feature.geometry;

      // Validar geometría vía parseGeometry (Point + MultiPoint N=1 — sub-0a)
      const pt = this.parseGeometry(geom, `Health feature ${String(feature.id ?? 'sin-id')}`);
      if (!pt) {
        console.warn('Feature sin geometría válida:', feature.id);
        return null;
      }

      const [x, y] = pt;

      // Validar coordenadas en rango UTM30 Andalucía
      if (x < 100000 || x > 800000 || y < 4000000 || y > 4300000) {
        console.warn('Coordenadas fuera de rango UTM30 Andalucía:', x, y);
        return null;
      }

      // Schema DERA G12 actual (smoke 16-may sub-WDGSDC): claves minúsculas.
      // Mantenemos fallback defensivo a mayúsculas legacy ISE para anti-regresión
      // (HX-FALLBACK-DEFENSIVO, 2.ª aplicación cross-geocoder tras WSSD).
      // `tipo` solo lo expone g12_02_Hospital_CAE — en CS llega como '' (no rompe).
      // `DISTRITO_SANITARIO` ya no existe en el schema actual; healthDistrict cae a ''.
      return {
        name: getStringProp(props, 'nombre', 'DENOMINACION', 'NOMBRE'),
        x,
        y,
        municipality: getStringProp(props, 'municipio', 'localidad', 'MUNICIPIO'),
        province: getStringProp(props, 'provincia', 'PROVINCIA'),
        address: getStringProp(props, 'direccion', 'DIRECCION', 'DOMICILIO'),
        properties: {
          facilityType: getStringProp(props, 'tipo', 'TIPO_CENTRO'),
          healthDistrict: getStringProp(props, 'DISTRITO_SANITARIO'),
          phone: getStringProp(props, 'telefono', 'TELEFONO'),
          website: getStringProp(props, 'web', 'WEB', 'URL'),
          ...props
        }
      };
    } catch (error) {
      console.error('Error parseando feature sanitaria:', error);
      return null;
    }
  }

  /**
   * Construye filtro CQL específico para centros sanitarios.
   *
   * sub-WDGSDC (OBS-FT-WFS-DERA-G12-SCHEMA-DRIFT-CROSS, 16-may): override completo
   * sin `super.buildCQLFilter()` para emitir nombres de campo en minúsculas
   * (schema real DERA G12) y priorizar `cod_mun='X'` cuando se aporta `codMun`.
   *
   * Whitelist `tipo` (HX-WHITELIST-FIELD-PER-LAYER, 1.ª aplicación):
   * - `g12_02_Hospital_CAE` SÍ expone el campo `tipo` → emitir filtro
   * - `g12_01_CentroSalud` NO tiene `tipo` → emitir filtro produce HTTP 400
   * - `DISTRITO_SANITARIO` ya no existe en el schema actual → eliminado
   *   (si se aporta `options.healthDistrict` se descarta silenciosamente)
   */
  protected buildCQLFilter(options: HealthSearchOptions): string {
    const filters: string[] = [];

    // Filtro municipal: cod_mun (INE) preferente sobre municipio ILIKE
    if (options.codMun) {
      filters.push(`cod_mun='${this.escapeCQL(options.codMun)}'`);
    } else if (options.municipality) {
      filters.push(`municipio ILIKE '%${this.escapeCQL(options.municipality)}%'`);
    }

    // Filtro provincial
    if (options.province) {
      filters.push(`provincia ILIKE '%${this.escapeCQL(options.province)}%'`);
    }

    // Filtro BBOX (heredado de la base, sintaxis idéntica)
    if (options.bbox) {
      const [minX, minY, maxX, maxY] = options.bbox;
      filters.push(`BBOX(the_geom,${minX},${minY},${maxX},${maxY},'EPSG:25830')`);
    }

    // Whitelist `tipo`: SOLO Hospital_CAE expone el campo (smoke 16-may sub-WDGSDC).
    // En g12_01_CentroSalud + g12_03_SedeDistritoSanidad emitirlo provoca HTTP 400.
    if (options.facilityType && this.config.layerName === 'g12_02_Hospital_CAE') {
      const typeMap: Record<HealthFacilityType, string> = {
        [HealthFacilityType.HEALTH_CENTER]: 'Centro de Salud',
        [HealthFacilityType.HOSPITAL]: 'Hospital',
        [HealthFacilityType.AMBULATORIO]: 'Ambulatorio'
      };
      const typeValue = typeMap[options.facilityType];
      filters.push(`tipo ILIKE '%${this.escapeCQL(typeValue)}%'`);
    }

    // DISTRITO_SANITARIO eliminado del schema DERA G12 actual (sub-WDGSDC 16-may).
    // No degradamos a otro campo — no hay análogo útil. Si DERA reintroduce
    // `distrito`, reactivar aquí con whitelist por capa.

    return filters.length > 0 ? filters.join(' AND ') : '';
  }

  /**
   * Geocodifica cambiando automáticamente entre capas según tipo detectado
   * 
   * Estrategia:
   * 1. Intenta con capa principal (g12_01_CentroSalud) — cubre CS + consultorios rurales consolidados
   * 2. Si falla y detecta "hospital" o "clínica" en nombre, intenta g12_02_Hospital_CAE
   */
  public async geocodeWithAutoLayer(options: HealthSearchOptions) {
    // Intento 1: Capa por defecto (Centros de Salud)
    let result = await this.geocodeLegacy(options);
    if (result && result.confidence >= 70) {
      return result;
    }

    // Detectar tipo en nombre para elegir capa alternativa
    const nameLower = options.name.toLowerCase();

    // Intento 2: Hospitales (capa canónica g12_02_Hospital_CAE en WFS DERA G12)
    if (nameLower.includes('hospital') || nameLower.includes('clínica')) {
      this.config.layerName = 'g12_02_Hospital_CAE';
      result = await this.geocodeLegacy(options);
      if (result && result.confidence >= 70) {
        this.config.layerName = 'g12_01_CentroSalud'; // Restaurar
        return result;
      }
    }

    // Nota: rama Consultorios eliminada — la capa g12_03_Consultorio no existe en
    // el WFS DERA G12 real (smoke 11-may). Los consultorios rurales del SAS están
    // consolidados bajo g12_01_CentroSalud, ya cubierto por el intento 1.
    // Investigación SIPOB/SAS pendiente en OBS-FT-SAN-CONSULTORIOS-FUENTE-AUSENTE.

    // Restaurar capa por defecto
    this.config.layerName = 'g12_01_CentroSalud';
    return result; // Retorna último intento (puede ser null)
  }

  /**
   * Obtiene todos los centros sanitarios de un municipio
   * Útil para pre-caching de municipios completos
   * 
   * @param municipality - Nombre del municipio
   * @param province - Nombre de la provincia
   * @returns Array de features sanitarias del municipio
   */
  public async getAllFacilitiesInMunicipality(
    municipality: string,
    province?: string
  ): Promise<WFSFeature[]> {
    try {
      const params = this.buildWFSParams({
        name: '', // Sin filtro por nombre
        municipality,
        province,
        maxResults: 500 // Aumentar límite para municipios grandes
      });

      const response = await this.axiosInstance.get(this.config.wfsEndpoint, { params });
      return this.parseResponse(response.data);

    } catch (error) {
      console.error(`Error obteniendo centros sanitarios de ${municipality}:`, error);
      return [];
    }
  }

  /**
   * Valida que unas coordenadas correspondan a un centro sanitario oficial
   * Útil para verificar coordenadas existentes en PTEL
   * 
   * @param x - Coordenada X (Este) en EPSG:25830
   * @param y - Coordenada Y (Norte) en EPSG:25830
   * @param radius - Radio de búsqueda en metros (default: 500m)
   * @returns Centro sanitario más cercano o null
   */
  public async validateCoordinates(
    x: number,
    y: number,
    radius: number = 500
  ): Promise<WFSFeature | null> {
    try {
      // Calcular BBOX alrededor del punto
      const bbox: [number, number, number, number] = [
        x - radius,
        y - radius,
        x + radius,
        y + radius
      ];

      const params = this.buildWFSParams({
        name: '',
        bbox,
        maxResults: 10
      });

      const response = await this.axiosInstance.get(this.config.wfsEndpoint, { params });
      const features = this.parseResponse(response.data);

      if (features.length === 0) {
        return null;
      }

      // Encontrar el más cercano
      let closest: WFSFeature | null = null;
      let minDistance = Infinity;

      for (const feature of features) {
        const distance = Math.sqrt(
          Math.pow(feature.x - x, 2) + Math.pow(feature.y - y, 2)
        );
        if (distance < minDistance) {
          minDistance = distance;
          closest = feature;
        }
      }

      return closest;

    } catch (error) {
      console.error('Error validando coordenadas sanitarias:', error);
      return null;
    }
  }
}
